import { create } from 'zustand';

import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import exportUtils from '@/features/Setting/ExportHistory/exportUtils';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { EXPORT_HISTORY_STATE } from '@/types/exportConst';
import { track } from '@/utils/track/track';

import usePopupStore from './pure/PopupStore';

type State = {
  exporting: boolean;
};

type Actions = {
  setState: StoreSetter<State>;
  onExportComplete: (log: ExportHistoryItem) => void;
  initExportStore: () => void;
};

const initialState: State = {
  exporting: false,
};

const useExportStore = create<State & Actions>((set, get) => ({
  ...initialState,
  onExportComplete(log) {
    set({
      exporting: false,
    });
    switch (log.state) {
      case EXPORT_HISTORY_STATE.COMPLETE: {
        track(AMP_KEYS.FEEDBACK_EXPORT_COMPLETE, {
          export_format: 'excel',
          has_filter: true, // exports always have filters applied
        });

        usePopupStore.getState().setPopup({
          popupTheme: 'positive',
          type: 'confirm',
          title: `Data export is ready.`,
          message: <ExportCompleteMessage />,
          confirmText: 'Download',
          cancelText: 'Cancel',
          onConfirm: async () => {
            await exportUtils.downloadMultipleFiles(log.alias, log.files);
          },
        });
        break;
      }
      case EXPORT_HISTORY_STATE.FAILED: {
        usePopupStore.getState().setPopup({
          popupTheme: 'negative',
          type: 'alert',
          title: `Data export failed.`,
          message:
            'Please try again. If the problem persists, please contact support.',
        });
        break;
      }
      case EXPORT_HISTORY_STATE.EXPORTING:
        break;
    }

    // Track feedback export complete event
  },
  setState: set,
  initExportStore: () => set(initialState),
}));

const ExportCompleteMessage = () => {
  const wr = useWorkspaceRouter();
  const color = useColor();
  return (
    <Typo type="body2">
      You can also export data from
      <br />
      <Typo
        color={color.primary100}
        style={{ textDecoration: 'underline', cursor: 'pointer' }}
        onClick={() => {
          wr.push('/settings/export-history');
        }}
      >
        Settings &gt; Export history page.
      </Typo>
    </Typo>
  );
};

export default useExportStore;
